import React, { useCallback, useRef, useState } from 'react';
import Portal from 'react-scale/corp/components/SmartLink/PreviewPortal';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { useAsync } from 'react-use-v13';
import { IUserJson } from '@scale/shared/types/user';
import { Button, Chip, Tooltip } from 'design-system';
import moment from 'moment';
import { ReviewLevel } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import TaskerPermissionsModal from 'react-scale/components/scalers/TaskerPermissionsModal';
import IsomorphicLink from 'react-scale/corp/components/UI/IsomorphicLink';
import TaskMetadataPopover from 'react-scale/components/scalers/tasks/TaskMetadataPopover';

interface ITaskAttemptLite {
  attemptedBy: string;
  attemptedAtReviewLevel: ReviewLevel;
  attemptedAt: string;
  _id: string;
}

interface ITaskPermissionsEntryLite {
  canAttempt: boolean;
  canReview: boolean;
  corpReviewSampleRate: number;
  createdAt: string;
  disableReason: string;
  disabled: boolean;
  lastActive: string;
  lastDisabledAt: string;
  permissionGroup: any;
  reviewLevel: ReviewLevel;
  suspended: boolean;
  taskCustomer: string;
  taskType: TaskType;
  worker: string;
}
interface ITaskerContextPaneProps {
  attempt: ITaskAttemptLite;
  projectId: string;
  hideLifetimeEarnings?: boolean;
  taskMetadata?: Record<string, any>;
  hideTaskerInfo?: boolean;
}
const TaskerContextPane: React.FC<ITaskerContextPaneProps> = ({
  attempt,
  projectId,
  hideLifetimeEarnings,
  taskMetadata,
  hideTaskerInfo,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [coords, setCoords] = useState<DOMRect | undefined>();
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const taskerPaneClass = 'tasker-context-head';

  const openPane = useCallback(
    (e: $FIXME) => {
      // Determine the top left corner of our bounding box
      let target: HTMLElement | null = e.target as HTMLDivElement;
      while (target) {
        if ([...(target.classList || [])].includes(taskerPaneClass)) break;
        target = target.parentElement;
      }
      if (!target) return; // Unable to determine container element?

      setOpen(o => !o);
      setCoords(target.getBoundingClientRect());
      e.stopPropagation();
    },
    [setOpen, setCoords],
  );

  const {
    value: taskerForProject,
    loading,
    error,
  } = useAsync(async () =>
    !hideTaskerInfo
      ? apiFetch<{ tasker: IUserJson; permissions: any }>(
          null,
          `corp-api/attemptAudit/taskerForProject/${projectId}/${attempt.attemptedBy}`,
        )
      : null,
  );
  const formatReviewLevel = (r: ReviewLevel) => (r === ReviewLevel.Attempt ? 'Attempt' : 'L' + r);

  return (
    <div className={'tasker-context'}>
      <style jsx>{`
        .tasker-context {
          display: flex;
          align-items: center;
          margin-right: 10px;
        }
        .tasker-context-head {
          display: flex;
          padding: 5px;
          border: 1px solid #ccc;
          box-sizing: border-box;
          align-items: center;
        }

        .tasker-context-head__icon {
          color: #888;
        }

        .tasker-context-head:hover {
          cursor: pointer;
        }

        .tasker-context-head:hover .tasker-context-head__icon {
          color: black;
        }

        .tasker-pane {
          position: absolute;
          border: 1px solid #ccc;
          opacity: 0.95;
          padding: 10px;
          box-shadow: 3px 3px 3px #eee;
          border-radius: 5px;
          cursor: default;
          color: black;
          width: auto;
          background: white;
          z-index: 9999;
        }

        .stats {
        }

        .stats > div {
          display: flex;
        }

        .stats > div > strong {
          min-width: 200px;
          text-align: right;
          margin-right: 10px;
        }
      `}</style>
      <div ref={wrapperRef} className={taskerPaneClass}>
        {taskMetadata && (
          <TaskMetadataPopover fromReviewHeader={false} taskMetadata={taskMetadata} />
        )}
        <div
          style={{ display: 'flex', flexDirection: 'column', marginRight: '10px' }}
          onClick={openPane}
        >
          <span
            style={{
              color: 'gray',
              fontSize: 'smaller',
              fontWeight: 100,
              textAlign: 'right',
              marginBottom: '-5px',
            }}
          >
            About this tasker
          </span>
          {(taskerForProject || hideTaskerInfo) && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Chip color={'primary'} label={formatReviewLevel(attempt.attemptedAtReviewLevel)} />
              <span style={{ marginLeft: '10px' }}>
                {hideTaskerInfo ? attempt.attemptedBy : taskerForProject!.tasker.email}
              </span>
            </div>
          )}
          {loading && <span style={{ marginRight: '10px' }}>Getting tasker information...</span>}
          {error && <span style={{ marginRight: '10px' }}>Sorry, there was an error.</span>}
        </div>
      </div>
      {taskerForProject && open && coords && (
        <Portal
          onOutsideClick={() => {
            !permissionsModalOpen && setOpen(false);
          }}
        >
          <div
            className="tasker-pane"
            style={
              coords
                ? {
                    top: coords.bottom + 'px',
                    right: window.innerWidth - coords.right + 'px',
                  }
                : {}
            }
          >
            <div className="stats">
              {!!attempt.attemptedAt && (
                <div>
                  <strong>Task attempted at</strong>
                  <Tooltip placement={'top'} title={new Date(attempt.attemptedAt).toLocaleString()}>
                    <span>{moment(attempt.attemptedAt).fromNow()}</span>
                  </Tooltip>
                </div>
              )}
              {!!taskerForProject.tasker.createdDate && (
                <div>
                  <strong>Tasker Joined</strong>
                  <Tooltip
                    placement={'top'}
                    title={new Date(taskerForProject.tasker.createdDate).toLocaleString()}
                  >
                    <span>{moment(taskerForProject.tasker.createdDate).fromNow()}</span>
                  </Tooltip>
                </div>
              )}
              {!hideLifetimeEarnings &&
                !!taskerForProject.tasker.cachedStats?.estTotalPayoutCents && (
                  <div>
                    <strong>Lifetime earnings</strong> $
                    {(taskerForProject.tasker.cachedStats?.estTotalPayoutCents / 100).toFixed(2)}
                  </div>
                )}
            </div>
            <PermissionsForProject
              permissions={taskerForProject.permissions}
              tasker={taskerForProject.tasker}
              permissionsModalOpen={permissionsModalOpen}
              setPermissionsModalOpen={setPermissionsModalOpen}
            />
            <div style={{ display: 'flex', marginTop: '5px', justifyContent: 'flex-end' }}>
              <IsomorphicLink target="_blank" href={'/lookup/' + taskerForProject.tasker._id}>
                <Button style={{ marginRight: '5px' }}>Tasker Lookup</Button>
              </IsomorphicLink>
              <IsomorphicLink target="_blank" href={'/lookup/' + attempt._id}>
                <Button>About This Attempt</Button>
              </IsomorphicLink>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

const PermissionsForProject: React.FC<{
  tasker: any;
  permissions: ITaskPermissionsEntryLite[];
  permissionsModalOpen: boolean;
  setPermissionsModalOpen: (o: boolean) => void;
}> = ({ permissions, tasker, setPermissionsModalOpen, permissionsModalOpen }) => {
  const permissionGroup = permissions.length && permissions[0].permissionGroup._id;
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
      <TaskerPermissionsModal
        loggedUserIsAdmin={true}
        isModalOpen={permissionsModalOpen}
        hideModal={() => setPermissionsModalOpen(false)}
        scaler={tasker}
        filter={{
          permissionGroup,
        }}
      />
      <Button type="primary" onClick={() => setPermissionsModalOpen(true)}>
        Edit project permissions
      </Button>
    </div>
  );
};

export default TaskerContextPane;
